import React, { useState, useEffect } from 'react';

import Footer from '../../components/footer/index';
import { useDarkMode } from '../../components/darkmode/DarkModeContext';
import { Monitor, Printer, Laptop, Server, Cpu, HardDrive, Mouse, Headphones, Zap, CheckCircle, Clock, Users, Shield, MessageCircle } from 'lucide-react';

const ComputerEquipmentPage = () => {
  // Use dark mode from context instead of local state
  const { isDarkMode } = useDarkMode();
  const [activeCategory, setActiveCategory] = useState('computers');

  // Fungsi untuk mengarahkan ke WhatsApp dengan pesan otomatis
  const contactViaWhatsApp = (productName = "Perangkat Komputer") => {
    const phoneNumber = "6287778882022"; // Ganti dengan nomor WhatsApp perusahaan
    const message = `Halo, saya tertarik dengan produk ${productName}. Bisakah saya mendapatkan informasi lebih lanjut mengenai spesifikasi dan harganya?`;
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${phoneNumber}?text=${encodedMessage}`, '_blank');
  };

  const categories = [
    { id: 'computers', name: 'Komputer & Laptop', icon: <Laptop className="w-6 h-6" /> },
    { id: 'printers', name: 'Printer', icon: <Printer className="w-6 h-6" /> },
    { id: 'ups', name: 'UPS & Power', icon: <Zap className="w-6 h-6" /> },
    { id: 'accessories', name: 'Aksesoris', icon: <Mouse className="w-6 h-6" /> },
    { id: 'networking', name: 'Jaringan', icon: <Server className="w-6 h-6" /> }
  ];

  const products = {
    computers: [
      {
        name: "Desktop Workstation",
        description: "Komputer desktop performa tinggi untuk kebutuhan profesional dan bisnis",
        image: "/image/layanan/desktop-workstation.png",
        specs: ["Intel Core i7/i9", "16GB-32GB RAM", "SSD 512GB-1TB", "GPU Dedicated"],
        brands: ["Dell", "HP", "Lenovo", "Asus"]
      },
      {
        name: "Business Laptop",
        description: "Laptop bisnis yang andal dengan keamanan enterprise dan daya tahan tinggi",
        image: "/image/layanan/business-laptop.png",
        specs: ["Intel Core i5/i7", "8GB-16GB RAM", "SSD 256GB-512GB", "Windows 11 Pro"],
        brands: ["Dell Latitude", "HP EliteBook", "Lenovo ThinkPad"]
      },
      {
        name: "All-in-One PC",
        description: "Solusi hemat space dengan layar dan komputer dalam satu unit",
        image: "/image/layanan/all-in-one-pc.png",
        specs: ["Intel Core i5/i7", "8GB-16GB RAM", "SSD 256GB-1TB", "Layar 21.5-27 inch"],
        brands: ["Dell", "HP", "Lenovo", "Apple iMac"]
      },
      {
        name: "Gaming PC",
        description: "Komputer gaming dengan performa maksimal untuk gaming dan content creation",
        image: "/image/layanan/gaming-pc.png",
        specs: ["Intel Core i7/i9 atau Ryzen 7/9", "16GB-32GB RAM", "GPU High-End", "SSD NVMe"],
        brands: ["Asus ROG", "MSI", "Acer Predator", "Custom Build"]
      }
    ],
    printers: [
      {
        name: "Laser Printer",
        description: "Printer laser untuk cetak dokumen berkualitas tinggi dengan kecepatan optimal",
        image: "/image/layanan/laser-printer.png",
        specs: ["Monochrome/Color", "Print Speed up to 40ppm", "Network Ready", "Duplex Printing"],
        brands: ["HP LaserJet", "Brother", "Canon", "Epson"]
      },
      {
        name: "Inkjet Printer",
        description: "Printer inkjet untuk cetak dokumen dan foto dengan kualitas warna yang hidup",
        image: "/image/layanan/inkjet-printer.png",
        specs: ["Color Printing", "Photo Quality", "Multi-function", "Wireless Connectivity"],
        brands: ["Epson", "Canon PIXMA", "HP DeskJet", "Brother"]
      },
      {
        name: "Multifunction Printer",
        description: "All-in-one: print, scan, copy, dan fax dalam satu perangkat",
        image: "/image/layanan/multifunction-printer.png",
        specs: ["Print/Scan/Copy/Fax", "Automatic Document Feeder", "Cloud Printing", "Mobile Printing"],
        brands: ["HP OfficeJet", "Epson WorkForce", "Canon MAXIFY", "Brother MFC"]
      },
      {
        name: "Label Printer",
        description: "Printer khusus untuk mencetak label dengan berbagai ukuran dan material",
        image: "/image/layanan/label-printer.png",
        specs: ["Thermal Printing", "Various Label Sizes", "Barcode Printing", "Portable Options"],
        brands: ["Brother", "Dymo", "Zebra", "Epson"]
      }
    ],
    ups: [
      {
        name: "Line Interactive UPS",
        description: "UPS untuk melindungi perangkat dari fluktuasi daya dan blackout",
        image: "/image/layanan/line-interactive-ups.png",
        specs: ["500VA-3000VA", "Voltage Regulation", "Battery Backup", "Surge Protection"],
        brands: ["APC", "CyberPower", "Eaton", "Tripp Lite"]
      },
      {
        name: "Online UPS",
        description: "UPS double conversion untuk perlindungan daya terbaik untuk server dan equipment critical",
        image: "/image/layanan/online-ups.png",
        specs: ["1000VA-5000VA", "Pure Sine Wave", "Zero Transfer Time", "Extended Runtime Options"],
        brands: ["APC Smart-UPS", "Eaton 9PX", "CyberPower OL", "Vertiv Liebert"]
      },
      {
        name: "Backup Power Supply",
        description: "Solusi power backup untuk perangkat jaringan dan komunikasi",
        image: "/image/layanan/backup-power.png",
        specs: ["DC Power Output", "Rackmount Options", "Extended Runtime", "Network Management"],
        brands: ["APC", "CyberPower", "Tripp Lite", "Minuteman"]
      },
      {
        name: "Power Distribution Unit (PDU)",
        description: "PDU untuk manajemen daya rack server dan perangkat jaringan",
        image: "/image/layanan/pdu.png",
        specs: ["Basic/Metered/Switched", "12-48 Outlets", "Current Monitoring", "Remote Management"],
        brands: ["APC", "CyberPower", "Tripp Lite", "Server Technology"]
      }
    ],
    accessories: [
      {
        name: "Monitor LED",
        description: "Monitor komputer berbagai ukuran dan resolusi untuk kebutuhan kerja",
        image: "/image/layanan/led-monitor.png",
        specs: ["21.5-32 inch", "Full HD/4K", "IPS/VA Panel", "Adjustable Stand"],
        brands: ["Dell", "HP", "LG", "Samsung", "Asus"]
      },
      {
        name: "Keyboard & Mouse",
        description: "Keyboard dan mouse ergonomis untuk kenyamanan bekerja seharian",
        image: "/image/layanan/keyboard-mouse.png",
        specs: ["Wired/Wireless", "Ergonomic Design", "Multi-device Connectivity", "Long Battery Life"],
        brands: ["Logitech", "Microsoft", "Dell", "HP"]
      },
      {
        name: "External Storage",
        description: "Hard drive eksternal dan SSD portable untuk backup dan transfer data",
        image: "/image/layanan/external-storage.png",
        specs: ["HDD/SSD", "500GB-5TB Capacity", "USB 3.0/3.1/Type-C", "Portable/Docking Station"],
        brands: ["Western Digital", "Seagate", "Samsung", "Toshiba"]
      },
      {
        name: "Webcam & Headset",
        description: "Perangkat video conference dan komunikasi untuk kerja remote",
        image: "/image/layanan/webcam-headset.png",
        specs: ["HD/4K Webcam", "Noise-cancelling Mic", "USB Connectivity", "Comfortable Design"],
        brands: ["Logitech", "Jabra", "Poly", "Microsoft"]
      }
    ],
    networking: [
      {
        name: "Network Switch",
        description: "Switch jaringan untuk menghubungkan perangkat dalam jaringan lokal",
        image: "/image/layanan/network-switch.png",
        specs: ["8-48 Ports", "Managed/Unmanaged", "Gigabit Ethernet", "PoE Options"],
        brands: ["Cisco", "TP-Link", "Netgear", "D-Link", "Ruijie"]
      },
      {
        name: "Wireless Router",
        description: "Router Wi-Fi untuk jaringan nirkabel di kantor dan bisnis",
        image: "/image/layanan/wireless-router.png",
        specs: ["Wi-Fi 6/6E", "Dual/Tri-band", "MU-MIMO", "VPN Support"],
        brands: ["Cisco", "TP-Link", "Asus", "Netgear", "MikroTik"]
      },
      {
        name: "Access Point",
        description: "Access point untuk extended coverage Wi-Fi di area kantor",
        image: "/image/layanan/access-point.png",
        specs: ["Indoor/Outdoor", "PoE Powered", "Mesh Support", "Cloud Management"],
        brands: ["Ubiquiti", "TP-Link", "Cisco", "Aruba", "Ruijie"]
      },
      {
        name: "Network Storage (NAS)",
        description: "Network Attached Storage untuk penyimpanan terpusat dan backup",
        image: "/image/layanan/nas.png",
        specs: ["2-8 Bays", "RAID Support", "Remote Access", "Backup Solutions"],
        brands: ["Synology", "QNAP", "Western Digital", "Asustor"]
      }
    ]
  };

  const benefits = [
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: "Produk Original",
      description: "Semua produk kami 100% original dengan garansi resmi"
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Pengiriman Cepat",
      description: "Pesanan diproses dan dikirim dalam waktu 1-2 hari kerja"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Konsultasi Gratis",
      description: "Tim ahli siap membantu memilih produk yang tepat untuk kebutuhan Anda"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Garansi Lengkap",
      description: "Dukungan garansi dan after-sales service yang terpercaya"
    },
    {
      icon: <Headphones className="w-6 h-6" />,
      title: "Dukungan Teknis",
      description: "Bantuan teknis dan instalasi oleh profesional berpengalaman"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Harga Kompetitif",
      description: "Harga terbaik dengan kualitas produk dan layanan premium"
    }
  ];

  const brands = [
    { name: "Dell", logo: "/brands/dell.png" },
    { name: "HP", logo: "/brands/hp.png" },
    { name: "Lenovo", logo: "/brands/lenovo.png" },
    { name: "Asus", logo: "/brands/asus.png" },
    { name: "Epson", logo: "/brands/epson.png" },
    { name: "Canon", logo: "/brands/canon.png" },
    { name: "Logitech", logo: "/brands/logitech.png" },
    { name: "APC", logo: "/brands/apc.png" },
    { name: "Cisco", logo: "/brands/cisco.png" },
    { name: "Synology", logo: "/brands/synology.png" }
  ];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-900' : 'bg-[#dfe4e0ff]'
    }`}>
      
      
      {/* Hero Section */}
      <section
        className={`py-20 px-4 relative overflow-hidden ${
          isDarkMode ? 'bg-gray-900' : ''
        }`}
        style={{
          backgroundColor: isDarkMode ? '#111827' : '#dfe4e0ff',
          backgroundImage: isDarkMode
            ? "url('/image/computer-equipment-dark.jpg')"
            : "url('/image/computer-equipment.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className={`absolute inset-0 ${
          isDarkMode 
            ? 'bg-gradient-to-r from-gray-900/80 to-gray-800/80' 
            : 'bg-gradient-to-r from-[#1E9B4C]/20 to-green-700/20'
        }`}></div>

        <div className="container mx-auto relative z-10">
          <div className="text-center mb-12">
            <div className={`inline-flex items-center gap-2 ${
              isDarkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-[#1E9B4C]'
            } px-4 py-2 rounded-full text-sm font-medium mb-6`}>
              <Monitor className="w-4 h-4" />
              IT Equipment Solutions
            </div>

            <h1
              className={`text-4xl md:text-6xl font-bold mb-6 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}
            >
              Penyediaan Perangkat
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#1E9B4C] to-green-700">
                Komputer & Printer
              </span>
            </h1>

            <p
              className={`text-xl max-w-3xl mx-auto mb-8 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}
            >
              Kami menyediakan berbagai perangkat keras komputer, termasuk komputer desktop, 
              laptop, printer, Uninterruptible Power Supply (UPS), dan perangkat pendukung 
              lainnya untuk memenuhi kebutuhan operasional bisnis Anda.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => contactViaWhatsApp("Perangkat Komputer")}
                className="bg-[#1E9B4C] hover:bg-green-700 text-white font-semibold px-8 py-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
              >
                <MessageCircle className="w-5 h-5" />
                Konsultasi Gratis
              </button>
            </div>
          </div>
        </div>
      </section>

       <div className="absolute inset-0">
        <img
          src="https://telnet.net.id/assets/bg.png"
          alt="Background pattern"
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/40 to-transparent dark:via-black/40"></div>
      </div>

      {/* Categories Section - Mobile Responsive with Horizontal Scroll */}
      <section 
        className={`py-16 px-4 bg-center relative overflow-hidden transition-colors duration-300 ${
          isDarkMode ? 'bg-gray-800' : 'bg-white'
        }`}
      >
        <div className={`absolute inset-0 ${
          isDarkMode 
            ? 'bg-gradient-to-r from-gray-900/80 to-gray-800/80' 
            : 'bg-gradient-to-r from-[#1E9B4C]/10 to-green-700/10'
        }`}></div>

        <div className="container mx-auto relative z-10">
          <div className="text-center mb-12">
            <h2 className={`text-3xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Kategori Produk
            </h2>
            <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Temukan berbagai perangkat yang kami sediakan untuk mendukung bisnis Anda
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-[#1E9B4C] to-green-600 mx-auto rounded-full mt-4"></div>
          </div>

          {/* Desktop Grid - Hidden on mobile */}
          <div className="hidden md:grid grid-cols-3 lg:grid-cols-5 gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`p-6 rounded-xl transition-all duration-300 ${
                  activeCategory === category.id
                    ? 'bg-[#1E9B4C] text-white shadow-lg transform scale-105'
                    : isDarkMode
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-green-50 hover:text-[#1E9B4C]'
                }`}
              >
                <div className="flex flex-col items-center">
                  <div className="mb-3">
                    {category.icon}
                  </div>
                  <span className="font-medium text-sm text-center">{category.name}</span>
                </div>
              </button>
            ))}
          </div>

          {/* Mobile Horizontal Scroll - Visible on mobile only */}
          <div className="md:hidden">
            <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`flex-shrink-0 p-4 rounded-xl transition-all duration-300 min-w-[140px] ${
                    activeCategory === category.id
                      ? 'bg-[#1E9B4C] text-white shadow-lg'
                      : isDarkMode
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-100 text-gray-700 hover:bg-green-50'
                  }`}
                >
                  <div className="flex flex-col items-center">
                    <div className="mb-2">
                      {React.cloneElement(category.icon, { className: "w-5 h-5" })}
                    </div>
                    <span className="font-medium text-xs text-center leading-tight">{category.name}</span>
                  </div>
                </button>
              ))}
            </div>
            
            {/* Custom scrollbar styles for mobile */}
            <style jsx>{`
              .scrollbar-hide {
                -ms-overflow-style: none;
                scrollbar-width: none;
              }
              .scrollbar-hide::-webkit-scrollbar {
                display: none;
              }
            `}</style>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className={`py-16 px-4 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-3xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {categories.find(cat => cat.id === activeCategory)?.name}
            </h2>
            <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Pilihan terbaik untuk kebutuhan {categories.find(cat => cat.id === activeCategory)?.name.toLowerCase()} Anda
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {products[activeCategory].map((product, index) => (
              <div
                key={index}
                className={`rounded-xl overflow-hidden shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105 ${
                  isDarkMode 
                    ? 'bg-gray-800 border border-gray-700' 
                    : 'bg-white border border-gray-100'
                }`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${
                    isDarkMode ? 'from-gray-900/70 to-transparent' : 'from-black/40 to-transparent'
                  }`}></div>
                </div>
                
                <div className="p-6">
                  <h3 className={`text-xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {product.name}
                  </h3>
                  <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {product.description}
                  </p>
                  
                  <div className="mb-4">
                    <h4 className={`text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Spesifikasi:
                    </h4>
                    <ul className="space-y-1">
                      {product.specs.map((spec, idx) => (
                        <li key={idx} className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} flex items-center gap-2`}>
                          <div className={`w-1.5 h-1.5 rounded-full ${isDarkMode ? 'bg-green-500' : 'bg-[#1E9B4C]'}`}></div>
                          {spec}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="mb-6">
                    <h4 className={`text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Brands:
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {product.brands.map((brand, idx) => (
                        <span
                          key={idx}
                          className={`px-2 py-1 rounded-full text-xs ${
                            isDarkMode
                              ? 'bg-gray-700 text-gray-300'
                              : 'bg-green-100 text-green-800'
                          }`}
                        >
                          {brand}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <button
                    onClick={() => contactViaWhatsApp(product.name)}
                    className={`w-full py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                      isDarkMode
                        ? 'bg-green-700 hover:bg-green-600 text-white'
                        : 'bg-[#1E9B4C] hover:bg-green-700 text-white'
                    }`}
                  >
                    <MessageCircle className="w-4 h-4" />
                    Minta Penawaran
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className={`py-16 px-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className={`text-3xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Mengapa Memilih Kami?
            </h2>
            <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Keunggulan layanan penyediaan perangkat dari kami
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-[#1E9B4C] to-green-600 mx-auto rounded-full mt-4"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className={`p-6 rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-xl ${
                  isDarkMode
                    ? 'bg-gray-700 border border-gray-600 hover:border-[#1E9B4C]'
                    : 'bg-gray-50 border border-gray-100 hover:border-[#1E9B4C]'
                }`}
              >
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${
                  isDarkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-[#1E9B4C]'
                }`}>
                  {benefit.icon}
                </div>
                <h3 className={`text-xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {benefit.title}
                </h3>
                <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section
        className={`py-16 px-4 transition-colors duration-300 ${
          isDarkMode ? 'bg-gray-800' : 'bg-[#1E9B4C]'
        }`}
      >
        <div className="container mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Siap Melengkapi Peralatan IT Anda?
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Hubungi kami sekarang untuk konsultasi gratis dan dapatkan penawaran terbaik untuk perangkat komputer dan printer kebutuhan bisnis Anda.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => contactViaWhatsApp("Perangkat Komputer")}
              className="bg-white text-[#1E9B4C] hover:bg-gray-100 font-semibold px-8 py-4 rounded-lg transition-colors duration-200 flex items-center gap-2 justify-center"
            >
              <MessageCircle className="w-5 h-5" />
              Konsultasi Gratis
            </button>
            <button 
              onClick={() => contactViaWhatsApp("Katalog Produk")}
              className="border-2 border-white text-white hover:bg-white hover:text-[#1E9B4C] font-semibold px-8 py-4 rounded-lg transition-colors duration-200 flex items-center gap-2 justify-center"
            >
              <Monitor className="w-5 h-5" />
              Lihat Katalog Lengkap
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ComputerEquipmentPage;